import * as turf from '@turf/turf';

import {ItemsTileItem} from './ItemsTileItem';
import {ItemsTile} from '../ItemsTile';

export class Label extends ItemsTileItem {

  public static readonly TOP_LEFT      = 'TOP_LEFT';
  public static readonly TOP_CENTER    = 'TOP_CENTER';
  public static readonly TOP_RIGHT     = 'TOP_RIGHT';

  public static readonly CENTER_LEFT   = 'CENTER_LEFT';
  public static readonly CENTER        = 'CENTER';
  public static readonly CENTER_RIGHT  = 'CENTER_RIGHT';

  public static readonly BOTTOM_LEFT   = 'BOTTOM_LEFT';
  public static readonly BOTTOM_CENTER = 'BOTTOM_CENTER';
  public static readonly BOTTOM_RIGHT  = 'BOTTOM_RIGHT';

  private static readonly OFFSET_LAT_LNG = {lat: 0.00015, lng: 0.00015};

  private value = undefined;
  private anchor = undefined;
  private drawFunction = undefined;

  private readonly scale = 4;

  constructor(config) {
    super(config);
    this.parseConfig();
  }

  public getId() {
    return this.config.id;
  }

  private resize(n) {
    return Math.round(n / this.scale * 100) / 100;
  }

  public draw(context, tile: ItemsTile, offsetX = 0, offsetY = 0) {
    const xy = this.latLngToCanvasXY(tile, this.latLngs[0]);
    // const context = tile.getContext();

    const offset = this.getAnchorOffset(tile);
    xy.x += offset.x + offsetX;
    xy.y += offset.y + offsetY;

    // this.drawDefault(context, xy);
    // this.drawVanilla(context, xy);
    // this.drawPrimary(context, xy);
    // this.drawEbony(context, xy);
    // this.drawBlackBackground(context, xy);
    // this.drawWhiteBackground(context, xy);

    this.drawFunction(context, xy);
  }

  private drawBlackBackground(context, xy) {
    this.drawBackground(context, xy, 'rgba(0, 0, 0, 0.6)', 'rgb(255, 255, 255)');
  }

  private drawWhiteBackground(context, xy) {
    this.drawBackground(context, xy, 'rgba(255, 255, 255, 0.6)', 'rgb(0, 0, 0)');
  }

  private drawBackground(context, xy, backgroundColor, color) {
    context.font = `bold ${this.resize(16)}px Monospace`;
    const text = this.config.value;
    const textWidth = context.measureText(text).width;

    context.beginPath();
    context.fillStyle = backgroundColor;
    context.roundRect(
      xy.x - textWidth / 2 - this.resize(16),
      xy.y - this.resize(16),
      textWidth + this.resize(32),
      this.resize(32),
      [this.resize(3)]
    );
    context.fill();

    context.fillStyle = color;
    context.textBaseline = 'middle';
    context.textAlign = 'center';
    context.fillText(text, xy.x, xy.y);
  }

  private drawPrimary(context, xy) {
    context.font = `bold ${this.resize(16)}px Monospace`;
    const text = this.config.value;
    const textWidth = context.measureText(text).width;

    context.beginPath();
    context.fillStyle = 'rgb(33, 33, 33)';
    context.fillRect(
      xy.x - textWidth / 2 - this.resize(16),
      xy.y - this.resize(16),
      textWidth + this.resize(32),
      this.resize(32)
    );
    context.fill();

    context.fillStyle = 'rgb(231, 228, 223)';
    context.textBaseline = 'middle';
    context.textAlign = 'center';
    context.fillText(text, xy.x, xy.y);
  }

  private drawVanilla(context, xy) {
    this.drawText(context, xy, 'white');
  }

  private drawEbony(context, xy) {
    this.drawText(context, xy, 'black');
  }

  private drawText(context, xy, color) {
    context.font = `bold ${this.resize(16)}px Monospace`;
    context.fillStyle = color;
    context.textBaseline = 'middle';
    context.textAlign = 'center';
    context.fillText(this.config.value, xy.x, xy.y);
  }

  private drawDefault(context, xy) {
    context.font = `bold ${this.resize(16)}px Monospace`;
    const text = this.config.value;
    const textWidth = context.measureText(text).width;

    context.beginPath();
    context.fillStyle = 'rgb(233, 233, 233)';
    context.ellipse(xy.x, xy.y, textWidth / 2 + this.resize(20) , this.resize(20), 0, 0, 2 * Math.PI);
    context.fill();

    context.fillStyle = 'rgb(33, 37, 41)';
    context.textBaseline = 'middle';
    context.textAlign = 'center';
    context.fillText(text, xy.x, xy.y);
  }

  public drawStrokeRed(context, xy) {
    context.font = `bold ${this.resize(16)}px Monospace`;
    const text = this.config.value;
    const textWidth = context.measureText(text).width;

    context.beginPath();
    context.strokeStyle = 'red';
    context.fillStyle = 'white';
    context.lineWidth = this.resize(1);
    context.ellipse(xy.x, xy.y, textWidth + this.resize(20) , this.resize(20), 0, 0, 2 * Math.PI);
    context.fill();
    context.stroke();

    context.textBaseline = 'middle';
    context.textAlign = 'center';
    context.fillStyle = 'black';
    context.fillText(text, xy.x, xy.y);
    context.stroke();
  }

  private getAnchorOffset(tile: ItemsTile) {
    let x = 0;
    let y = 0;

    const zeroXY = tile.latLangToPx({lat: 0, lng: 0});
    const offsetXY = tile.latLangToPx({lat: Label.OFFSET_LAT_LNG.lat, lng: Label.OFFSET_LAT_LNG.lng});
    offsetXY.x -= zeroXY.x;
    offsetXY.y -= zeroXY.y;

    if (this.anchor === Label.TOP_LEFT || this.anchor === Label.CENTER_LEFT || this.anchor === Label.BOTTOM_LEFT) {
      x = -offsetXY.x;
    }
    if (this.anchor === Label.TOP_RIGHT || this.anchor === Label.CENTER_RIGHT || this.anchor === Label.BOTTOM_RIGHT) {
      x = offsetXY.x;
    }
    if (this.anchor === Label.TOP_LEFT || this.anchor === Label.TOP_CENTER || this.anchor === Label.TOP_RIGHT) {
      y = offsetXY.y;
    }
    if (this.anchor === Label.BOTTOM_LEFT || this.anchor === Label.BOTTOM_CENTER || this.anchor === Label.BOTTOM_RIGHT) {
      y = -offsetXY.y;
    }

    return {x, y};
  }

  protected createTurfFigure() {
    //TODO: possible return full label rect, not only center
    return turf.point([this.latLngs[0].lng, this.latLngs[0].lat]);
  }

  protected parseConfig() {
    if ( typeof this.config.value === 'undefined' || typeof this.config.anchor === 'undefined') {
      throw new Error('Invalid config format');
    }

    this.value = this.config.value;
    this.anchor = this.config.anchor;

    this.drawFunction = this.drawDefault;
    if (typeof this.config.style !== 'undefined' && this.config.style) {
       this.drawFunction = this.getStyleFunction(this.config.style);
    }
  }

  private getStyleFunction(style) {
    if (style.indexOf('ebony') !== -1) {
      return this.drawEbony;
    } else if (style.indexOf('vanilla') !== -1) {
      return this.drawVanilla;
    } else if (style.indexOf('primary') !== -1) {
      return this.drawPrimary;
    } else if (style.indexOf('white') !== -1) {
      return this.drawWhiteBackground;
    } else if (style.indexOf('black') !== -1) {
      return this.drawBlackBackground;
    }
    return this.drawDefault;
  }

  protected parseLatLngs() {
    if ( typeof this.config.position === 'undefined'
      || typeof this.config.position.latitude === 'undefined'
      || typeof this.config.position.longitude === 'undefined'
    ) {
      throw new Error('Invalid position format');
    }

    return [{ lat: this.config.position.latitude, lng: this.config.position.longitude }];
  }

}
